import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import {
  Box,
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CancelIcon,
} from "@material-ui/icons";

const PRIORITY_ORDER = ["critical", "high", "medium", "low"];

const PRIORITY_COLORS = {
  critical: "#d32f2f",
  high: "#f57c00",
  medium: "#1976d2",
  low: "#388e3c",
};

const GLOBAL_CONFIG_LABELS = {
  acknowledgement_hours: {
    label: "Ticket Acknowledgement",
    description: "Time allowed for agents to review, assign priority, and assign an agent to new tickets.",
  },
};

const DAYS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

const TIMEZONES = [
  "Asia/Kuala_Lumpur",
  "Asia/Singapore",
  "Asia/Jakarta",
  "Asia/Bangkok",
  "Asia/Hong_Kong",
  "Asia/Tokyo",
  "Asia/Manila",
  "Asia/Kolkata",
  "Australia/Sydney",
  "Europe/London",
  "America/New_York",
  "America/Los_Angeles",
  "UTC",
];

const formatHours = (hours, type) => {
  const suffix = type === "calendar" ? " calendar" : " business";
  if (hours < 24) return `${hours}h${suffix}`;
  const days = hours / 24;
  const daysStr = Number.isInteger(days) ? `${days}d` : `${days.toFixed(1)}d`;
  return `${hours}h${suffix} (~${daysStr})`;
};

const toTimeInput = (t) => (t ? String(t).slice(0, 5) : "09:00");

const HoursInput = ({ value, type, onChangeValue, onChangeType }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <TextField
      size="small"
      type="number"
      value={value}
      onChange={(e) => onChangeValue(e.target.value)}
      inputProps={{ min: 1 }}
      sx={{ width: 90 }}
    />
    <Typography variant="body2" color="textSecondary">hrs</Typography>
    <Select
      size="small"
      value={type || "business"}
      onChange={(e) => onChangeType(e.target.value)}
      sx={{ minWidth: 120 }}
    >
      <MenuItem value="business">Business Hours</MenuItem>
      <MenuItem value="calendar">Calendar Hours</MenuItem>
    </Select>
  </Box>
);

const fmtDateRange = (start, end) => {
  const s = (start || "").slice(0, 10);
  const e = (end || "").slice(0, 10);
  if (!s) return "";
  if (s === e) return s;
  return `${s} → ${e}`;
};

const SlaSettingsPage = ({
  slaConfigs,
  globalSlaConfigs = [],
  businessHours,
  holidays = [],
  holidayDialog,
  editingPriority,
  editValues,
  editingGlobalKey,
  editGlobalValues,
  editingBusinessHours,
  bhDraft,
  canEdit,
  onEdit,
  onCancel,
  onSave,
  onChange,
  onEditGlobal,
  onCancelGlobal,
  onSaveGlobal,
  onChangeGlobal,
  onEditBusinessHours,
  onCancelBusinessHours,
  onSaveBusinessHours,
  onChangeBusinessHours,
  onOpenAddHoliday,
  onOpenEditHoliday,
  onDeleteHoliday,
  onCloseHolidayDialog,
  onChangeHolidayField,
  onSaveHoliday,
  autoReplies = [],
  editingAutoReplyContext,
  autoReplyDraft,
  onEditAutoReply,
  onCancelAutoReply,
  onChangeAutoReply,
  onSaveAutoReply,
}) => {
  const sortedConfigs = [...slaConfigs].sort(
    (a, b) => PRIORITY_ORDER.indexOf(a.priority) - PRIORITY_ORDER.indexOf(b.priority)
  );

  return (
    <>
      <Helmet>
        <title>SLA Settings | Kood Asia Management Portal</title>
      </Helmet>
      <Box sx={{ backgroundColor: "background.default", minHeight: "100%", py: 3 }}>
        <Container maxWidth={false}>
          <Card sx={{ mb: 2 }}>
            <Typography variant="h4" sx={{ padding: 2 }}>
              SLA Settings
            </Typography>
            <Typography variant="body2" sx={{ px: 2, pb: 2, color: "text.secondary" }}>
              Configure SLA targets. New tickets will use the current configuration at the time of creation / acknowledgement.
            </Typography>
          </Card>

          {/* Global SLA Configs */}
          {globalSlaConfigs.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Global SLA Targets
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                  Applies to all tickets regardless of priority.
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: "22%" }}>SLA Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "28%" }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "35%" }}>Target</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "15%", textAlign: "right" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {globalSlaConfigs.map((config) => {
                      const meta = GLOBAL_CONFIG_LABELS[config.key] || { label: config.key, description: "" };
                      const isEditing = editingGlobalKey === config.key;
                      return (
                        <TableRow key={config.key}>
                          <TableCell>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {meta.label}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                              {meta.description}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {isEditing ? (
                              <HoursInput
                                value={editGlobalValues.value}
                                type={editGlobalValues.hours_type}
                                onChangeValue={(v) => onChangeGlobal("value", v)}
                                onChangeType={(t) => onChangeGlobal("hours_type", t)}
                              />
                            ) : (
                              <Typography>{formatHours(config.value, config.hours_type)}</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: "right" }}>
                            {isEditing ? (
                              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  startIcon={<SaveIcon />}
                                  onClick={onSaveGlobal}
                                  disabled={!editGlobalValues.value || editGlobalValues.value < 1}
                                >
                                  Save
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<CancelIcon />}
                                  onClick={onCancelGlobal}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            ) : canEdit ? (
                              <IconButton size="small" onClick={() => onEditGlobal(config)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}

          {/* Per-Priority SLA Configs */}
          <Card sx={{ mb: 3 }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Priority-Based SLA Targets
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                First-response and resolution targets per priority. Each has its own Business/Calendar mode.
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: "15%" }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: "35%" }}>First Response Target</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: "35%" }}>Resolution Target</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: "15%", textAlign: "right" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedConfigs.map((config) => {
                    const isEditing = editingPriority === config.priority;
                    return (
                      <TableRow key={config.priority}>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                backgroundColor: PRIORITY_COLORS[config.priority] || "#999",
                              }}
                            />
                            <Typography variant="body1" sx={{ fontWeight: 500, textTransform: "capitalize" }}>
                              {config.priority}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <HoursInput
                              value={editValues.response_hours}
                              type={editValues.response_hours_type}
                              onChangeValue={(v) => onChange("response_hours", v)}
                              onChangeType={(t) => onChange("response_hours_type", t)}
                            />
                          ) : (
                            <Typography>{formatHours(config.response_hours, config.response_hours_type)}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <HoursInput
                              value={editValues.resolution_hours}
                              type={editValues.resolution_hours_type}
                              onChangeValue={(v) => onChange("resolution_hours", v)}
                              onChangeType={(t) => onChange("resolution_hours_type", t)}
                            />
                          ) : (
                            <Typography>{formatHours(config.resolution_hours, config.resolution_hours_type)}</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ textAlign: "right" }}>
                          {isEditing ? (
                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<SaveIcon />}
                                onClick={onSave}
                                disabled={
                                  !editValues.response_hours ||
                                  !editValues.resolution_hours ||
                                  editValues.response_hours < 1 ||
                                  editValues.resolution_hours < 1
                                }
                              >
                                Save
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CancelIcon />}
                                onClick={onCancel}
                              >
                                Cancel
                              </Button>
                            </Box>
                          ) : canEdit ? (
                            <IconButton size="small" onClick={() => onEdit(config)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {sortedConfigs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: "center", py: 4 }}>
                        <Typography color="textSecondary">No SLA configurations found.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Business Hours Config */}
          <Card>
            <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Business Hours Configuration
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  These hours are applied to every SLA entry configured as &quot;Business Hours&quot;.
                </Typography>
              </Box>
              {canEdit && !editingBusinessHours && businessHours && (
                <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={onEditBusinessHours}>
                  Edit
                </Button>
              )}
              {editingBusinessHours && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button variant="contained" color="primary" size="small" startIcon={<SaveIcon />} onClick={onSaveBusinessHours}>
                    Save
                  </Button>
                  <Button variant="outlined" size="small" startIcon={<CancelIcon />} onClick={onCancelBusinessHours}>
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>
            {!businessHours ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography color="textSecondary">Loading business hours...</Typography>
              </Box>
            ) : (
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <Typography sx={{ fontWeight: 500, minWidth: 100 }}>Timezone</Typography>
                  {editingBusinessHours ? (
                    <Select
                      size="small"
                      value={bhDraft?.timezone || "Asia/Kuala_Lumpur"}
                      onChange={(e) => onChangeBusinessHours("timezone", e.target.value)}
                      sx={{ minWidth: 220 }}
                    >
                      {TIMEZONES.map((tz) => (
                        <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Typography>{businessHours.timezone}</Typography>
                  )}
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, width: "20%" }}>Day</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: "20%" }}>Working</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: "30%" }}>Start</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: "30%" }}>End</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {DAYS.map((d) => {
                        const source = editingBusinessHours ? bhDraft : businessHours;
                        const enabled = !!(source && source[`${d.key}_enabled`]);
                        const start = toTimeInput(source && source[`${d.key}_start`]);
                        const end = toTimeInput(source && source[`${d.key}_end`]);
                        return (
                          <TableRow key={d.key}>
                            <TableCell>{d.label}</TableCell>
                            <TableCell>
                              {editingBusinessHours ? (
                                <Switch
                                  size="small"
                                  checked={enabled}
                                  onChange={(e) => onChangeBusinessHours(`${d.key}_enabled`, e.target.checked)}
                                />
                              ) : (
                                <Typography sx={{ color: enabled ? "#388e3c" : "#b71c1c" }}>
                                  {enabled ? "Yes" : "Closed"}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {editingBusinessHours ? (
                                <TextField
                                  size="small"
                                  type="time"
                                  value={start}
                                  disabled={!enabled}
                                  onChange={(e) => onChangeBusinessHours(`${d.key}_start`, `${e.target.value}:00`)}
                                  sx={{ width: 150 }}
                                />
                              ) : (
                                <Typography sx={{ color: enabled ? "inherit" : "text.disabled" }}>
                                  {enabled ? start : "—"}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {editingBusinessHours ? (
                                <TextField
                                  size="small"
                                  type="time"
                                  value={end}
                                  disabled={!enabled}
                                  onChange={(e) => onChangeBusinessHours(`${d.key}_end`, `${e.target.value}:00`)}
                                  sx={{ width: 150 }}
                                />
                              ) : (
                                <Typography sx={{ color: enabled ? "inherit" : "text.disabled" }}>
                                  {enabled ? end : "—"}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Card>

          {/* Holidays */}
          <Card sx={{ mt: 3 }}>
            <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Holidays
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Days or ranges that are skipped when counting Business Hours SLA (even if the weekday is a working day).
                </Typography>
              </Box>
              {canEdit && (
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onOpenAddHoliday}>
                  Add Holiday
                </Button>
              )}
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: "40%" }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: "30%" }}>Date(s)</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: "15%" }}>Active</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: "15%", textAlign: "right" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {holidays.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: "center", py: 4 }}>
                        <Typography color="textSecondary">No holidays configured.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    holidays.map((h) => (
                      <TableRow key={h.id}>
                        <TableCell>{h.name}</TableCell>
                        <TableCell>{fmtDateRange(h.start_date, h.end_date)}</TableCell>
                        <TableCell>
                          <Typography sx={{ color: h.is_active ? "#388e3c" : "#b71c1c" }}>
                            {h.is_active ? "Yes" : "No"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: "right" }}>
                          {canEdit && (
                            <>
                              <IconButton size="small" onClick={() => onOpenEditHoliday(h)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => onDeleteHoliday(h)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Auto-reply Messages */}
          <Card sx={{ mt: 3 }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Auto-reply Messages
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Automatic first response posted after a customer creates a ticket. The correct
                message is chosen based on Business Hours (and holidays). Revealed to the customer
                with a 2-second typing delay.
              </Typography>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2, p: 2, pt: 0 }}>
              {["office_hours", "outside_office_hours"].map((ctx) => {
                const cfg = autoReplies.find((r) => r.context === ctx);
                const label = ctx === "office_hours" ? "During office hours" : "Outside office hours";
                const isEditing = editingAutoReplyContext === ctx;
                if (!cfg) return null;
                return (
                  <Box
                    key={ctx}
                    sx={{
                      border: "1px solid #E2E8F0",
                      borderRadius: "8px",
                      p: 2,
                      backgroundColor: isEditing ? "#F8FAFC" : "#FFFFFF",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {ctx === "office_hours"
                            ? "Sent when a ticket is created during configured working hours."
                            : "Sent when a ticket is created outside working hours, on holidays, or on non-working days."}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {!isEditing && (
                          <Typography variant="caption" sx={{ color: cfg.is_active ? "#388e3c" : "#b71c1c", fontWeight: 600 }}>
                            {cfg.is_active ? "ACTIVE" : "DISABLED"}
                          </Typography>
                        )}
                        {!isEditing && canEdit && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => onEditAutoReply(cfg)}
                          >
                            Edit
                          </Button>
                        )}
                        {isEditing && (
                          <>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              startIcon={<SaveIcon />}
                              onClick={onSaveAutoReply}
                              disabled={!autoReplyDraft?.message?.trim()}
                            >
                              Save
                            </Button>
                            <Button variant="outlined" size="small" startIcon={<CancelIcon />} onClick={onCancelAutoReply}>
                              Cancel
                            </Button>
                          </>
                        )}
                      </Box>
                    </Box>
                    {isEditing ? (
                      <Box>
                        <TextField
                          value={autoReplyDraft?.message || ""}
                          onChange={(e) => onChangeAutoReply("message", e.target.value)}
                          multiline
                          minRows={3}
                          maxRows={8}
                          fullWidth
                          inputProps={{ maxLength: 4000 }}
                          helperText={`${(autoReplyDraft?.message || "").length}/4000`}
                        />
                        <FormControlLabel
                          sx={{ mt: 1 }}
                          control={
                            <Switch
                              size="small"
                              checked={!!autoReplyDraft?.is_active}
                              onChange={(e) => onChangeAutoReply("is_active", e.target.checked)}
                            />
                          }
                          label="Active"
                        />
                      </Box>
                    ) : (
                      <Typography sx={{ whiteSpace: "pre-wrap", color: cfg.is_active ? "#0F172A" : "#94A3B8" }}>
                        {cfg.message}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Card>
        </Container>
      </Box>

      {/* Holiday add/edit dialog */}
      <Dialog open={!!holidayDialog} onClose={onCloseHolidayDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{holidayDialog?.mode === "edit" ? "Edit Holiday" : "Add Holiday"}</DialogTitle>
        <DialogContent>
          {holidayDialog && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <TextField
                label="Name"
                value={holidayDialog.name}
                onChange={(e) => onChangeHolidayField("name", e.target.value)}
                fullWidth
                autoFocus
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={holidayDialog.start_date}
                  onChange={(e) => onChangeHolidayField("start_date", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={holidayDialog.end_date}
                  onChange={(e) => onChangeHolidayField("end_date", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!holidayDialog.is_active}
                    onChange={(e) => onChangeHolidayField("is_active", e.target.checked)}
                  />
                }
                label="Active"
              />
              <Typography variant="caption" color="textSecondary">
                Tip: Set the same start and end date for a single-day holiday, or pick a range for multi-day (e.g. Chinese New Year).
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseHolidayDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSaveHoliday}
            disabled={
              !holidayDialog?.name ||
              !holidayDialog?.start_date ||
              !holidayDialog?.end_date ||
              holidayDialog?.end_date < holidayDialog?.start_date
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

SlaSettingsPage.propTypes = {
  slaConfigs: PropTypes.array.isRequired,
  globalSlaConfigs: PropTypes.array,
  businessHours: PropTypes.object,
  editingPriority: PropTypes.string,
  editValues: PropTypes.object.isRequired,
  editingGlobalKey: PropTypes.string,
  editGlobalValues: PropTypes.object,
  editingBusinessHours: PropTypes.bool,
  bhDraft: PropTypes.object,
  canEdit: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onEditGlobal: PropTypes.func.isRequired,
  onCancelGlobal: PropTypes.func.isRequired,
  onSaveGlobal: PropTypes.func.isRequired,
  onChangeGlobal: PropTypes.func.isRequired,
  onEditBusinessHours: PropTypes.func.isRequired,
  onCancelBusinessHours: PropTypes.func.isRequired,
  onSaveBusinessHours: PropTypes.func.isRequired,
  onChangeBusinessHours: PropTypes.func.isRequired,
  holidays: PropTypes.array,
  holidayDialog: PropTypes.object,
  onOpenAddHoliday: PropTypes.func,
  onOpenEditHoliday: PropTypes.func,
  onDeleteHoliday: PropTypes.func,
  onCloseHolidayDialog: PropTypes.func,
  onChangeHolidayField: PropTypes.func,
  onSaveHoliday: PropTypes.func,
  autoReplies: PropTypes.array,
  editingAutoReplyContext: PropTypes.string,
  autoReplyDraft: PropTypes.object,
  onEditAutoReply: PropTypes.func,
  onCancelAutoReply: PropTypes.func,
  onChangeAutoReply: PropTypes.func,
  onSaveAutoReply: PropTypes.func,
};

export default SlaSettingsPage;
